import { InputNumber as AntdInputNumber } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { FormItemInputContext } from 'antd/es/form/context';
import classNames from 'classnames';
import React, { FC, KeyboardEvent, memo, ReactNode, useContext } from 'react';
import s from './index.module.scss';
import { Popup } from '../Popup';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';

interface InputNumberProps {
  value?: Nullable<number>;
  onChange?: (value: Nullable<number>) => void;
  placeholder?: string;
  prefix?: ReactNode;
  status?: 'warning' | 'error' | undefined;
  suffix?: ReactNode;
  addonAfter?: ReactNode;
  className?: string;
  bordered?: boolean;
  requiredMark?: boolean;
  isDisabled?: boolean;
  size?: SizeType;
  label?: string;
  tooltip?: string;
  containerClassName?: string;
  min?: number;
  max?: number;
}

export const InputNumber: FC<InputNumberProps> = memo((props) => {
  const {
    value,
    placeholder,
    prefix,
    suffix,
    addonAfter,
    className,
    onChange,
    bordered = false,
    requiredMark,
    isDisabled,
    size = 'middle',
    label,
    tooltip,
    containerClassName,
    status,
    min,
    max,
  } = props;

  const { status: formItemStatus } = useContext(FormItemInputContext);
  const isError = status === 'error' || formItemStatus === 'error';

  const handleChange = (value: Nullable<number>): void => {
    onChange?.(value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === ',') {
      event.preventDefault();
    }
  };

  return (
    <div className={classNames('flex flex-col', containerClassName)}>
      <div className="flex">
        {label && <div className="mb-2.5 mr-2">{label}</div>}
        {tooltip && (
          <Popup title={label} description={tooltip}>
            <InfoIcon width={22} height={22} className="[&>path]:stroke-primaryLight" />
          </Popup>
        )}
      </div>
      {requiredMark && <div className="text-error text-2xl absolute z-10 top-0 left-1 pointer-events-none">*</div>}
      <AntdInputNumber
        className={classNames(s.inputNumber, s[size], className)}
        status={isError ? 'error' : status}
        variant={bordered ? 'outlined' : 'borderless'}
        placeholder={placeholder}
        prefix={prefix}
        suffix={suffix}
        value={value}
        onChange={handleChange}
        controls={false}
        disabled={isDisabled}
        addonAfter={addonAfter}
        onKeyDown={handleKeyDown}
        max={max}
        min={min}
      />
    </div>
  );
});
